import { call, put, select } from 'redux-saga/effects';
import {
  deleteExcelContactFailure,
  deleteExcelContactSuccess,
  fetchOrganisationContactsRequest,
} from '../actions';
import { deleteExcelContact, getAllExcelTransactions } from '../api';
import { DeleteExcelContactRequestAction } from '../types';
import { ApiError, ApiResponse } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { logoutRequest } from 'store/auth/actions';
import { MessageData } from 'store/app/types';
import { turnMessageOn } from 'store/app/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { MessageStates } from 'containers/MessageBox';
import { getOrganisationViesContacts } from '../selectors';
import {
  buildContactUsageApiFilterAttempts,
} from 'utils/contact-transaction-usage';
import { Contact } from 'store/vat-checker/models';
import i18n from 'i18next';

function* contactHasAssignedTransactions(
  organisationId: string,
  contact: Contact
): Generator<any, boolean, any> {
  const filterAttempts = buildContactUsageApiFilterAttempts(contact);
  for (const filters of filterAttempts) {
    const response = yield call(
      getAllExcelTransactions,
      organisationId,
      1,
      1,
      filters
    );
    const total = response?.data?.transactions?.totalElements ?? 0;
    if (total > 0) return true;
  }
  return false;
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* deleteExcelContactSaga(
  action: DeleteExcelContactRequestAction
) {
  try {
    const { organisationId, contactUuid } = action.payload;

    const organisationViesContacts = yield select(getOrganisationViesContacts);
    const matchedContact =
      organisationViesContacts?.contacts?.find(
        (contact: Contact) =>
          contact.contactUuid === contactUuid ||
          contact.providerContactId === contactUuid
      ) ?? ({ contactUuid } as Contact);

    const isUsed: boolean = yield call(
      contactHasAssignedTransactions,
      organisationId,
      matchedContact
    );
    if (isUsed) {
      const message: MessageData = {
        title: i18n.t('notifications.cannot-delete-contact'),
        description: i18n.t('notifications.this-contact-is-assigned-to-one-or-more-transactions-remove-it-f'),
        type: MessageStates.ERROR,
      };
      yield put(turnMessageOn(message));
      yield put(deleteExcelContactFailure());
      return;
    }

    const response: ApiResponse = yield call(
      deleteExcelContact,
      organisationId,
      contactUuid
    );

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    yield put(deleteExcelContactSuccess());

    const message: MessageData = {
      title: i18n.t('common.success'),
      description: response.responseMessage.message,
      type: MessageStates.SUCCESS,
    };
    yield put(turnMessageOn(message));
    yield put(fetchOrganisationContactsRequest(organisationId));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(deleteExcelContactFailure());
  }
}
